import axios from "axios";
import { AuthorizationErrorResponseDtoCodeEnum } from "generated";

export const KNOWN_APP_ERROR_CODES = Object.values(
  AuthorizationErrorResponseDtoCodeEnum,
);

export type KnownAppErrorCode = AuthorizationErrorResponseDtoCodeEnum;
export type AppErrorCode = KnownAppErrorCode | "UNKNOWN";
export type RouteErrorScope =
  | "upload"
  | "comments"
  | "admin"
  | "metadata"
  | "overview";

export function isKnownAuthCode(code: AppErrorCode): code is KnownAppErrorCode {
  return code !== "UNKNOWN";
}

export class AppRouteError extends Error {
  readonly code: AppErrorCode;
  readonly scope: RouteErrorScope;
  readonly status?: number;
  readonly uploadId?: number;
  readonly detail?: string;
  readonly title?: string;
  readonly body?: string;

  constructor(init: {
    code: AppErrorCode;
    scope: RouteErrorScope;
    status?: number;
    uploadId?: number;
    detail?: string;
    title?: string;
    body?: string;
  }) {
    super(init.code);
    this.code = init.code;
    this.scope = init.scope;
    this.status = init.status;
    this.uploadId = init.uploadId;
    this.detail = init.detail;
    this.title = init.title;
    this.body = init.body;
  }
}

export function toRouteError(
  error: unknown,
  ctx: { scope: RouteErrorScope; uploadId?: number },
): AppRouteError {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data as
      | { code?: string; uploadId?: number; title?: string; body?: string }
      | undefined;
    const code =
      data?.code &&
      (KNOWN_APP_ERROR_CODES as readonly string[]).includes(data.code)
        ? (data.code as AppErrorCode)
        : "UNKNOWN";

    return new AppRouteError({
      code,
      scope: ctx.scope,
      status,
      uploadId: data?.uploadId ?? ctx.uploadId,
      detail:
        code === "UNKNOWN" ? (error.message ?? "Unknown error") : undefined,
      title: code === "UNKNOWN" ? undefined : data?.title,
      body: code === "UNKNOWN" ? undefined : data?.body,
    });
  }

  return new AppRouteError({
    code: "UNKNOWN",
    scope: ctx.scope,
    uploadId: ctx.uploadId,
    detail: error instanceof Error ? error.message : String(error),
  });
}

export function classifyRouteError(error: unknown): AppRouteError {
  if (error instanceof AppRouteError) return error;
  if (axios.isAxiosError(error)) {
    return toRouteError(error, { scope: "overview" });
  }

  const routerErr = error as
    | { statusText?: string; message?: string }
    | undefined;
  return new AppRouteError({
    code: "UNKNOWN",
    scope: "overview",
    detail: routerErr?.statusText || routerErr?.message || "Unknown error",
  });
}
